import axiosInstance, { extractErrorMessage } from './axiosConfig';
import { Tag, TagCreate, Agent, AgentCreate, AgentUpdate } from '../types/agent';
import { KnowledgeBaseDetailType } from '../types/knowledgeBase';
import { PaginatedResponse } from '../types/user';
import { BaseResponse, DataSourceResponse } from '../types/dataSource';

// Tag operations

/**
 * Fetch all tags
 * @returns Promise with array of tags
 */
export const getAllTags = async (): Promise<Tag[]> => {
  try {
    const response = await axiosInstance.get('/agents/tags');
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar tags'
      )
    );
  }
};

/**
 * Create a new tag
 * @param data Tag creation data
 * @returns Promise with created tag data
 */
export const createTag = async (data: TagCreate): Promise<Tag> => {
  try {
    const response = await axiosInstance.post('/agents/tags', data);
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao criar tag'
      )
    );
  }
};

/**
 * Delete a tag
 * @param tagId Tag ID to delete
 * @returns Promise with success response
 */
export const deleteTag = async (tagId: number): Promise<boolean> => {
  try {
    const response = await axiosInstance.delete(`/agents/tags/${tagId}`);
    return response.data.success;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao excluir tag'
      )
    );
  }
};

/**
 * Add a tag to an agent
 * @param agentId Agent ID to update
 * @param tagId Tag ID to add
 * @returns Promise with updated agent data
 */
export const addTagToAgent = async (agentId: number, tagId: number): Promise<Agent> => {
  try {
    const response = await axiosInstance.post(`/agents/${agentId}/tags/${tagId}`);
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao adicionar tag ao agente'
      )
    );
  }
};

/**
 * Remove a tag from an agent
 * @param agentId Agent ID to update
 * @param tagId Tag ID to remove
 * @returns Promise with updated agent data
 */
export const removeTagFromAgent = async (agentId: number, tagId: number): Promise<Agent> => {
  try {
    const response = await axiosInstance.delete(`/agents/${agentId}/tags/${tagId}`);
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao remover tag do agente'
      )
    );
  }
};

// List all agents with pagination and search
export const listAgents = async (
  page: number = 1,
  limit: number = 50,
  search?: string,
  deployedOnly: boolean = false,
  tagIds?: number[],
  signal?: AbortSignal,
  workspaceId?: number, // Optional: omit to get agents from ALL accessible workspaces (TD users)
  visibilityLevel?: string // Optional: filter by visibility level ('global', 'workspace', 'group')
): Promise<PaginatedResponse<Agent>> => {
  try {
    const skip = (page - 1) * limit;

    if (tagIds && tagIds.length > 0) {
      // Create the URL with tag_ids repeated for each ID
      let url = `/agents?skip=${skip}&limit=${limit}&deployed_only=${deployedOnly}`;

      // Only add workspace_id if provided
      if (workspaceId !== undefined) {
        url += `&workspace_id=${workspaceId}`;
      }

      // Add visibility_level filter if provided
      if (visibilityLevel) {
        url += `&visibility_level=${visibilityLevel}`;
      }

      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      tagIds.forEach(id => {
        url += `&tag_ids=${id}`;
      });

      const response = await axiosInstance.get(url, { signal });
      return response.data;
    } else {
      // Use normal params object if no tag_ids
      const params: Record<string, unknown> = {
        skip,
        limit,
        deployed_only: deployedOnly,
      };

      // Only add workspace_id if provided (undefined = fetch from all accessible workspaces)
      if (workspaceId !== undefined) {
        params.workspace_id = workspaceId;
      }

      // Add visibility_level filter if provided
      if (visibilityLevel) {
        params.visibility_level = visibilityLevel;
      }

      if (search) {
        params.search = search;
      }

      const response = await axiosInstance.get('/agents', { params, signal });
      return response.data;
    }
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar agentes'
      )
    );
  }
};

// List deployed agents for regular users
export const listDeployedAgents = async (
  skip: number = 0,
  limit: number = 50
): Promise<PaginatedResponse<Agent>> => {
  try {
    const params = { skip, limit };
    const response = await axiosInstance.get('/agents/deployed', { params });
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar agentes implantados'
      )
    );
  }
};

// Get single agent details
export const getAgent = async (id: number): Promise<BaseResponse<Agent>> => {
  try {
    const response = await axiosInstance.get(`/agents/${id}`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar agente'
      )
    );
  }
};

// Create new agent
export const createAgent = async (data: AgentCreate): Promise<BaseResponse<Agent>> => {
  try {
    const response = await axiosInstance.post('/agents', data);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao criar agente'
      )
    );
  }
};

// Update agent
export const updateAgent = async (id: number, data: AgentUpdate): Promise<BaseResponse<Agent>> => {
  try {
    const response = await axiosInstance.put(`/agents/${id}`, data);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao atualizar agente'
      )
    );
  }
};

// Delete agent
export const deleteAgent = async (id: number): Promise<BaseResponse> => {
  try {
    const response = await axiosInstance.delete(`/agents/${id}`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao excluir agente'
      )
    );
  }
};

// Add knowledge base to agent
export const addKnowledgeBase = async (agentId: number, knowledgeBaseId: number): Promise<BaseResponse<Agent>> => {
  try {
    const response = await axiosInstance.post(
      `/agents/${agentId}/knowledge-bases/${knowledgeBaseId}`,
      {}
    );
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao adicionar base de conhecimento ao agente'
      )
    );
  }
};

// Remove knowledge base from agent
export const removeKnowledgeBase = async (agentId: number, knowledgeBaseId: number): Promise<BaseResponse<Agent>> => {
  try {
    const response = await axiosInstance.delete(`/agents/${agentId}/knowledge-bases/${knowledgeBaseId}`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao remover base de conhecimento do agente'
      )
    );
  }
};

// Get agent's knowledge bases
export const getAgentKnowledgeBases = async (agentId: number): Promise<BaseResponse<KnowledgeBaseDetailType[]>> => {
  try {
    const response = await axiosInstance.get(`/agents/${agentId}/knowledge-bases`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar bases de conhecimento do agente'
      )
    );
  }
};

// Toggle agent deployment status
export const toggleAgentDeployment = async (agentId: number): Promise<BaseResponse<Agent>> => {
  try {
    const response = await axiosInstance.post(`/agents/${agentId}/deploy`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao alternar implantação do agente'
      )
    );
  }
};

// Get agent's associated data sources
export const getAgentDataSources = async (agentId: number): Promise<BaseResponse<DataSourceResponse[]>> => {
  try {
    const response = await axiosInstance.get(`/agents/${agentId}/data-sources`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar fontes de dados do agente'
      )
    );
  }
};

/**
 * Fetches agents available to the current user based on their visibility level.
 *
 * @param skip - Number of records to skip (for pagination)
 * @param limit - Maximum number of records to return
 * @param searchTerm - Optional search term to filter agents by name or description
 * @param tagIds - Optional list of tag IDs to filter agents
 * @param visibilityLevel - Optional visibility filter: 'global', 'workspace', or 'group'
 * @param orderBy - Optional sort order: 'recent' or 'most_used'
 * @returns Promise with array of available agents and pagination metadata
 */
export const getAvailableAgents = async (
  skip: number = 0,
  limit: number = 50,
  searchTerm?: string,
  tagIds?: number[],
  visibilityLevel?: string,
  orderBy?: 'recent' | 'most_used'
): Promise<PaginatedResponse<Agent>> => {
  try {
    // Build URL with query parameters
    let url = `/agents/available?skip=${skip}&limit=${limit}`;

    if (searchTerm) {
      url += `&search=${encodeURIComponent(searchTerm)}`;
    }

    if (tagIds && tagIds.length > 0) {
      tagIds.forEach(id => {
        url += `&tag_ids=${id}`;
      });
    }

    if (visibilityLevel) {
      url += `&visibility_level=${visibilityLevel}`;
    }

    if (orderBy) {
      url += `&order_by=${orderBy}`;
    }

    const response = await axiosInstance.get(url);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao listar agentes disponíveis'
      )
    );
  }
};

/**
 * Fetches agents visible to user in a specific workspace.
 * Returns agents based on visibility rules (global, workspace, group).
 *
 * @param workspaceId - Workspace ID to list agents from
 * @param options - Query options
 * @returns Promise with array of visible agents and pagination metadata
 */
export const listAgentsByWorkspace = async (
  workspaceId: number,
  options: {
    deployed_only?: boolean;
    skip?: number;
    limit?: number;
    search?: string;
  } = {}
): Promise<PaginatedResponse<Agent>> => {
  try {
    const params: Record<string, unknown> = {
      workspace_id: workspaceId,
      deployed_only: options.deployed_only ?? false,
      skip: options.skip ?? 0,
      limit: options.limit ?? 50,
    };

    if (options.search) {
      params.search = options.search;
    }

    const response = await axiosInstance.get('/agents', { params });
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao listar agentes do workspace'
      )
    );
  }
};

// Agent selection redesign — pins e agentes frequentes

/**
 * Pin (favoritar) um agente para o usuário atual. Idempotente.
 */
export const pinAgent = async (agentId: number): Promise<boolean> => {
  try {
    const response = await axiosInstance.post(`/agents/${agentId}/pin`);
    return response.data.success;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao fixar agente'
      )
    );
  }
};

/**
 * Remove o pin do usuário atual em um agente. Idempotente.
 */
export const unpinAgent = async (agentId: number): Promise<boolean> => {
  try {
    const response = await axiosInstance.delete(`/agents/${agentId}/pin`);
    return response.data.success;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao desfixar agente'
      )
    );
  }
};

/**
 * Agentes frequentes do usuário (fixados primeiro, depois mais usados no mês).
 */
export const getFrequentAgents = async (limit: number = 5): Promise<Agent[]> => {
  try {
    const response = await axiosInstance.get(`/agents/frequent?limit=${limit}`);
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar agentes frequentes'
      )
    );
  }
};

// Global Agent Publication Operations

/**
 * Publish an agent globally (visible to all workspaces)
 * Only available for TD workspace
 * @param agentId - ID of the agent to publish
 * @returns Promise with published agent
 */
export const publishAgentGlobal = async (agentId: number): Promise<Agent> => {
  try {
    const response = await axiosInstance.post(`/agents/${agentId}/publish-global`);
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao publicar agente globalmente'
      )
    );
  }
};

/**
 * Unpublish a global agent (revert to workspace visibility)
 * Only available for TD workspace
 * @param agentId - ID of the agent to unpublish
 * @returns Promise with unpublished agent
 */
export const unpublishAgent = async (agentId: number): Promise<Agent> => {
  try {
    const response = await axiosInstance.post(`/agents/${agentId}/unpublish`);
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao despublicar agente'
      )
    );
  }
};